import { useEffect, useState } from 'react';
import { apiUrl } from '../../api/apiUrl';
import { Loader } from '../../components/loader/Loader';
import http from '../../api/axios';
import { CompanyTeamMember, Team } from '../../types/Team';
import { CompanyTeamAssignForm } from './CompanyTeamAssignForm';
import { handleApiErrors } from '../../api/apiErrorHandler';
import { toast } from 'react-toastify';
import DataTable from '../../components/table/DataTable';
import { columns } from '../../components/columns/Company/CompanyTeamsColumns';
import { paths } from '../../pages/RouterContainer';
import { useNavigate } from 'react-router-dom';
import { useLoading } from '../../context/loadingContext';

interface Props {
  companyId: number;
}

/**
 * Component for assigning team to company
 */
export const CompanyTeamAssign = ({ companyId }: Props) => {
  const { startLoading, stopLoading } = useLoading();
  const navigate = useNavigate();
  // All users teams - ready to assign to company
  const [userTeams, setUserTeams] = useState<Team[] | null>(null);
  // Teams assigned to company
  const [companyTeams, setCompanyTeams] = useState<CompanyTeamMember[] | null>(null);
  const isCurrent = window.location.pathname.includes(`current`);

  /**
   * Loads users teams. Ready to assing to company.
   */
  const fetchUserTeams = async () => {
    const response = await http.apiGet({ url: `${apiUrl.TEAM_GET_USERS_TEAMS}` });
    setUserTeams(response?.data);
  };

  /**
   * Loads company teams.
   */
  const fetchCompanyTeams = async () => {
    const response = await http.apiGet({
      url: `${apiUrl.COMPANY_TEAM_MEMBER_LIST}?parentId=${companyId}`,
    });
    setCompanyTeams(response?.data);
  };

  useEffect(() => {
    if (!companyId) return;
    fetchUserTeams();
    fetchCompanyTeams();
  }, [companyId]);

  /**
   * Handles assigning team to company
   * @param values
   */
  const onAssignTeam = async (selectedTeam: Team) => {
    if (!selectedTeam || !companyId) return;
    try {
      startLoading();
      const response = await http.apiPost({
        url: apiUrl.COMPANY_TEAM_MEMBER_CREATE,
        data: { companyId: companyId, teamId: selectedTeam.id },
      });
      if (response?.status === 201) {
        stopLoading();
        toast.success('Success!');
        fetchCompanyTeams();
      }
    } catch (err) {
      stopLoading();
      handleApiErrors(err);
    }
  };

  /**
   * Handles removing assigned team.
   * @param id assigned team
   */
  const onRemoveAssignedTeam = async (id: string | number) => {
    if (id === null) return;
    const response = await http.apiDelete({
      url: `${apiUrl.COMPANY_TEAM_MEMBER_DELETE}?id=${id}`,
    });
    if (response?.status === 204) {
      toast.success('Success!');
      fetchCompanyTeams();
    }
  };

  /**
   * Redirects to company team edit page
   * @param id
   */
  const onEditTeam = (id: string | number) => {
    if (!id) return;
    navigate(`/${paths.COMPANY_TEAMS}/${isCurrent ? `current` : companyId}/edit/${id}`);
  };

  const assignForm = () => {
    if (!userTeams) return <Loader />;
    if (userTeams.length === 0) return <>No teams to assing</>;
    return <CompanyTeamAssignForm teams={userTeams} onAssign={onAssignTeam} />;
  };

  return (
    <>
      {assignForm()}
      <DataTable
        data={companyTeams}
        columns={columns}
        handleEdit={onEditTeam}
        handleRemove={onRemoveAssignedTeam}
      />
    </>
  );
};
